/**
 * Application Status Dropdown — Candidate Status "Journey" stepper.
 *
 * Vertical stepper of stages (accordion). Each stage expands to its statuses.
 * Multi-round stages (Technical Interview) nest a second accordion per round:
 *   Stage → "L{n} Scheduled" (title) → L{n} Selected / Hold / Rejected (options).
 *
 * States: Completed (before current) ✓ disabled · Current highlighted ·
 * Next (valid transitions / AC2) selectable → mandatory-comment modal (AC3/AC5) ·
 * Upcoming greyed. Every change (esp. Hold/Reject/Withdraw) requires a comment,
 * which is written to the audit trail (AC4). Stages colour-coded by group (AC6).
 *
 * The selected value is a candidate_status_id.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useCandidateStages } from '../../hooks/useCandidateStages';
import { getStageColor } from '../../utils/stageColors';
import type { CandidateStatusFlat } from '../../types/candidateStatus';

interface ApplicationStatusDropdownProps {
  currentStatus: string | null;
  jobId: string;
  resumeId: string;
  /** Legacy application_status (old enum) — shown as "<value> (old)" until migrated. */
  oldStatus?: string | null;
  /** Match creation time (ISO) — used to tell legacy records from new ones. */
  matchCreatedAt?: string | null;
  onStatusChange: (newCandidateStatusId: string, comment: string) => void | Promise<void>;
  /** When true, the current status is still shown but the trigger is non-interactive. */
  disabled?: boolean;
}

type StepState = 'completed' | 'current' | 'next' | 'upcoming';
interface Round { key: string; scheduled?: CandidateStatusFlat; outcomes: CandidateStatusFlat[] }

// TEMP (business showcase): when true, every status is selectable regardless of
// the stage-aware transition graph. Set back to false to re-enforce valid-next flow.
const ENABLE_ALL_STATUSES = true;

// Pretty-print a legacy status code, e.g. "NOT_JOINED" -> "Not Joined".
const prettifyOldStatus = (code: string) =>
  code.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

// Feature go-live date. A record created BEFORE this is "legacy": a legacy MATCHED
// record shows "Matched (old)", while a newly-created MATCHED record shows "Not Set".
// ⚠️ Set this to your actual QA/prod go-live (deploy) date — everything created
// before it is treated as an old record needing migration.
const LEGACY_CUTOFF_MS = new Date('2026-08-06T00:00:00Z').getTime();

// Negative outcomes require a mandatory comment/reason (AC5).
const NEGATIVE_KEYWORDS = ['HOLD', 'REJECT', 'WITHDRAWN', 'NO_SHOW', 'DROPOUT', 'DECLINED', 'REVOKED', 'FAILED'];
// Status codes that never require a comment (even if they look negative).
const COMMENT_EXEMPT_CODES = ['SCREEN_REJECTED_BY_CLIENT'];
const isNegativeStatus = (code?: string) => {
  const c = (code || '').toUpperCase();
  if (COMMENT_EXEMPT_CODES.includes(c)) return false;
  return NEGATIVE_KEYWORDS.some((k) => c.includes(k));
};

const ApplicationStatusDropdown: React.FC<ApplicationStatusDropdownProps> = ({
  currentStatus,
  oldStatus,
  matchCreatedAt,
  onStatusChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'below' | 'above'>('below');
  const [buttonPosition, setButtonPosition] = useState<{ left: number; top: number; bottom: number } | null>(null);
  const [expandedStageIds, setExpandedStageIds] = useState<string[]>([]);
  const [expandedRounds, setExpandedRounds] = useState<string[]>([]);
  const [pendingStatus, setPendingStatus] = useState<CandidateStatusFlat | null>(null);
  const [comment, setComment] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const { stages, flatStatuses, statusById, allowedNextFor, isLoading, isError } = useCandidateStages();

  const stageMeta = useMemo(() => {
    const m = new Map<string, { name: string; code: string; order: number; canEdit: boolean }>();
    for (const s of stages) m.set(s.id, { name: s.stageName, code: s.stageCode, order: s.displayOrder, canEdit: s.canEdit ?? true });
    return m;
  }, [stages]);

  const current = currentStatus ? statusById.get(currentStatus) : undefined;
  // Show a legacy status ("Rejected (old)" / "Matched (old)") when there's no new
  // status yet. A *progressed* old value (anything except MATCHED) is always legacy.
  // A MATCHED value is legacy only if the match was created before go-live — new
  // candidates default to MATCHED and should still read "Not Set".
  const oldUpper = (oldStatus || '').toUpperCase();
  const isLegacyRecord = matchCreatedAt ? new Date(matchCreatedAt).getTime() < LEGACY_CUTOFF_MS : false;
  const showOld = !current && !!oldStatus && (oldUpper !== 'MATCHED' || isLegacyRecord);
  const oldColor = { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400' };
  const currentLabel = current
    ? current.statusName
    : showOld
    ? `${prettifyOldStatus(oldStatus!)} (old)`
    : 'Not Set';
  const currentColor = showOld ? oldColor : getStageColor(current?.stageCode);
  const currentStageOrder = current ? (stageMeta.get(current.stageId)?.order ?? -1) : -1;
  const currentStatusOrder = current?.displayOrder ?? -1;
  // The single stage immediately after the current one gets the "Next" badge
  // (rather than every stage that happens to contain a reachable status).
  const nextStageOrder = useMemo(() => {
    if (currentStageOrder < 0) return -1;
    const higher = stages.map((s) => s.displayOrder).filter((o) => o > currentStageOrder).sort((a, b) => a - b);
    return higher.length ? higher[0] : -1;
  }, [stages, currentStageOrder]);

  const allowedNext = useMemo(() => allowedNextFor(currentStatus), [allowedNextFor, currentStatus]);
  const allowedIds = useMemo(() => new Set(allowedNext.map((s) => s.id)), [allowedNext]);

  const steps = useMemo(() => {
    const byStage = new Map<string, CandidateStatusFlat[]>();
    for (const s of flatStatuses) {
      if (!byStage.has(s.stageId)) byStage.set(s.stageId, []);
      byStage.get(s.stageId)!.push(s);
    }
    return Array.from(byStage.entries()).map(([stageId, items]) => {
      const meta = stageMeta.get(stageId);
      const order = meta?.order ?? 0;
      const hasRounds = items.some((i) => /^L\d+_/.test(i.statusCode));
      let rounds: Round[] | null = null;
      let flatItems: CandidateStatusFlat[] | null = null;
      if (hasRounds) {
        const rmap = new Map<string, Round>();
        for (const item of items) {
          const m = item.statusCode.match(/^(L\d+)_/);
          const key = m ? m[1] : 'OTHER';
          if (!rmap.has(key)) rmap.set(key, { key, outcomes: [] });
          const r = rmap.get(key)!;
          if (item.statusCode.endsWith('_SCHEDULED')) r.scheduled = item;
          else r.outcomes.push(item);
        }
        rounds = Array.from(rmap.values());
      } else {
        flatItems = items;
      }
      let state: StepState;
      if (order < currentStageOrder) state = 'completed';
      else if (order === currentStageOrder) state = 'current';
      else if (order === nextStageOrder) state = 'next';
      else state = 'upcoming';
      return { stageId, stageName: meta?.name ?? '', stageCode: meta?.code ?? '', state, rounds, flatItems };
    });
  }, [flatStatuses, stageMeta, currentStageOrder, nextStageOrder, allowedIds]);

  const statusState = (s: CandidateStatusFlat): StepState => {
    if (s.id === currentStatus) return 'current';
    if (allowedIds.has(s.id)) return 'next';
    const order = stageMeta.get(s.stageId)?.order ?? 0;
    if (order < currentStageOrder) return 'completed';
    if (order === currentStageOrder && s.displayOrder < currentStatusOrder) return 'completed';
    return 'upcoming';
  };

  // Auto-expand current + next stages, and rounds containing current/next.
  // In "enable all" mode, expand everything so the full menu is visible.
  useEffect(() => {
    if (!isOpen) return;
    if (ENABLE_ALL_STATUSES) {
      setExpandedStageIds(steps.map((s) => s.stageId));
      const all: string[] = [];
      for (const s of steps) if (s.rounds) for (const r of s.rounds) all.push(`${s.stageId}:${r.key}`);
      setExpandedRounds(all);
      return;
    }
    setExpandedStageIds(steps.filter((s) => s.state === 'current' || s.state === 'next').map((s) => s.stageId));
    const rk: string[] = [];
    for (const s of steps) {
      if (!s.rounds) continue;
      for (const r of s.rounds) {
        const hasCurrent = r.scheduled?.id === currentStatus || r.outcomes.some((o) => o.id === currentStatus);
        const hasNext = (r.scheduled && allowedIds.has(r.scheduled.id)) || r.outcomes.some((o) => allowedIds.has(o.id));
        if (hasCurrent || hasNext) rk.push(`${s.stageId}:${r.key}`);
      }
    }
    setExpandedRounds(rk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggleStage = (id: string) =>
    setExpandedStageIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleRound = (rk: string) =>
    setExpandedRounds((p) => (p.includes(rk) ? p.filter((x) => x !== rk) : [...p, rk]));

  useEffect(() => {
    const handleScroll = (e: Event) => {
      if (isOpen) {
        if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
        if (buttonRef.current) computePanelPosition(buttonRef.current.getBoundingClientRect());
        else setIsOpen(false);
      }
    };
    if (isOpen) window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const computePanelPosition = useCallback((r: DOMRect) => {
    const vh = window.innerHeight, vw = window.innerWidth;
    const dh = 420, dw = 300;
    const below = vh - r.bottom, above = r.top;
    let left = r.left;
    if (left + dw > vw - 10) left = vw - dw - 10;
    if (left < 10) left = 10;
    setButtonPosition({ left, top: r.top, bottom: r.bottom });
    if (below < dh && above > dh) setDropdownPosition('above');
    else if (above < dh && below > dh) setDropdownPosition('below');
    else setDropdownPosition(above > below ? 'above' : 'below');
  }, []);

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isUpdating || disabled) return;
    computePanelPosition(e.currentTarget.getBoundingClientRect());
    setIsOpen(!isOpen);
  };

  // Apply a status change (shared by the direct path and the comment modal)
  const applyStatus = async (status: CandidateStatusFlat, commentText: string) => {
    setIsUpdating(true);
    try {
      await onStatusChange(status.id, commentText);
      toast.success(`Status updated to ${status.statusName}`);
      setPendingStatus(null);
      setComment('');
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePickStatus = (status: CandidateStatusFlat) => {
    if (!status.canEdit || status.id === currentStatus) return;
    setIsOpen(false);
    if (isNegativeStatus(status.statusCode)) {
      // Negative outcome → require a reason via the comment modal
      setComment('');
      setPendingStatus(status);
    } else {
      // Positive/neutral → apply immediately, no popup
      void applyStatus(status, '');
    }
  };

  const handleConfirm = async () => {
    if (!pendingStatus || isUpdating) return;
    if (isNegativeStatus(pendingStatus.statusCode) && !comment.trim()) return;
    await applyStatus(pendingStatus, comment.trim());
  };

  const handleCancelModal = () => {
    if (isUpdating) return;
    setPendingStatus(null);
    setComment('');
  };

  const CheckIcon = ({ cls }: { cls: string }) => (
    <svg className={`w-3.5 h-3.5 shrink-0 ml-2 ${cls}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
  );

  // A single selectable status row
  const renderStatusRow = (status: CandidateStatusFlat) => {
    const state = statusState(status);
    const isCurrent = state === 'current';
    const selectable = !isCurrent && status.canEdit && (ENABLE_ALL_STATUSES || state === 'next');
    let cls: string;
    if (isCurrent) {
      cls = 'bg-blue-50 border-blue-400 text-blue-900 font-medium cursor-default';
    } else if (state === 'completed') {
      cls = `bg-green-50 border-green-100 text-green-700 ${selectable ? 'hover:bg-green-100 cursor-pointer' : 'cursor-default'}`;
    } else if (selectable) {
      cls = 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50 hover:border-blue-300 cursor-pointer';
    } else {
      cls = 'bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed';
    }
    return (
      <button
        key={status.id}
        role="option"
        aria-selected={isCurrent}
        onClick={() => selectable && handlePickStatus(status)}
        disabled={!selectable}
        className={`w-full px-2.5 py-1.5 text-left text-xs rounded-lg border flex items-center justify-between transition-all duration-150 ${cls}`}
      >
        <span className="truncate">{status.statusName}</span>
        {state === 'completed' ? <CheckIcon cls="text-green-600" /> : isCurrent ? <CheckIcon cls="text-blue-600" /> : null}
      </button>
    );
  };

  // A round accordion: "L{n} Scheduled" is a SECTION TITLE (not selectable).
  // The selectable statuses are the outcomes (Selected / Hold / Rejected).
  const renderRound = (stageId: string, round: Round) => {
    const sched = round.scheduled;
    const state = sched ? statusState(sched) : 'upcoming';
    const rk = `${stageId}:${round.key}`;
    const expanded = expandedRounds.includes(rk);
    const headTint =
      state === 'current' ? 'bg-blue-50/60 border-blue-200'
      : state === 'completed' ? 'bg-green-50/60 border-green-100'
      : 'bg-gray-50 border-gray-200';
    const titleColor =
      state === 'current' ? 'text-blue-800'
      : state === 'completed' ? 'text-green-700'
      : 'text-gray-700';
    return (
      <div key={round.key}>
        {/* Round title — a section header, not a selectable status */}
        <button
          type="button"
          onClick={() => toggleRound(rk)}
          className={`w-full flex items-center gap-2 pl-2.5 pr-2 py-1.5 rounded-lg border transition-colors hover:border-gray-300 ${headTint}`}
        >
          <span className={`flex-1 text-left text-xs font-semibold truncate ${titleColor}`}>
            {sched?.statusName ?? round.key}
          </span>
          {state === 'completed' && <CheckIcon cls="text-green-600" />}
          {state === 'current' && <span className="text-[10px] font-semibold text-blue-600 shrink-0">Current</span>}
          <svg className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        {expanded && (
          <div className="mt-1 ml-2.5 pl-2.5 border-l border-gray-100 space-y-1">
            {round.outcomes.map(renderStatusRow)}
          </div>
        )}
      </div>
    );
  };

  const badge: Record<StepState, { label: string; cls: string } | null> = {
    completed: { label: 'Completed', cls: 'bg-green-100 text-green-700' },
    current: { label: 'Current', cls: 'bg-blue-100 text-blue-700' },
    next: { label: 'Next', cls: 'bg-blue-50 text-blue-600' },
    upcoming: null,
  };
  const iconCls: Record<StepState, string> = {
    completed: 'bg-green-500 text-white',
    current: 'bg-blue-600 text-white',
    next: 'bg-gray-100 text-gray-500',
    upcoming: 'bg-gray-100 text-gray-400',
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        disabled={isUpdating || isLoading || disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-busy={isLoading}
        className={`flex items-center justify-between gap-1.5 min-w-[7rem] px-2 py-1 rounded-lg border text-xs font-medium transition-colors ${currentColor.bg} ${currentColor.text} ${currentColor.border} ${
          disabled ? 'opacity-60 cursor-not-allowed' : isUpdating || isLoading ? 'opacity-60 cursor-wait' : 'hover:brightness-95 cursor-pointer'
        }`}
        title={disabled ? "You don't have permission to apply matching status" : isLoading ? 'Loading statuses…' : 'Apply matching status'}
      >
        {isLoading ? (
          <span className="flex items-center gap-1.5 text-gray-400">
            <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400" />
            <span>Loading…</span>
          </span>
        ) : (
          <span className="flex items-center gap-1 truncate">
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${currentColor.dot}`} />
            <span className="truncate">{currentLabel}</span>
          </span>
        )}
        {isUpdating ? (
          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current" />
        ) : !isLoading ? (
          <svg className="w-3.5 h-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        ) : null}
      </button>

      {isOpen && !isUpdating && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
          <div
            ref={panelRef}
            role="listbox"
            aria-label="Candidate status journey"
            className="fixed z-[99999] w-[300px] max-h-[420px] flex flex-col bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
            style={{
              left: `${buttonPosition?.left || 0}px`,
              ...(dropdownPosition === 'above'
                ? { bottom: `${window.innerHeight - (buttonPosition?.top || 0) + 8}px` }
                : { top: `${(buttonPosition?.bottom || 0) + 8}px` }),
            }}
          >
            <div className="px-3 py-2 border-b border-gray-100 bg-white shrink-0">
              <span className="text-xs text-gray-500">Current Status: </span>
              <span className="text-xs font-semibold text-gray-900">{currentLabel}</span>
            </div>

            <div className="flex-1 overflow-y-auto p-2 scroll-smooth space-y-1.5">
              {isLoading && <div className="px-2 py-2 text-sm text-gray-500">Loading statuses…</div>}
              {isError && !isLoading && <div className="px-2 py-2 text-sm text-red-600">Failed to load statuses</div>}
              {!isLoading && !isError && steps.map((step) => {
                // Only show Completed/Current/Next badges once a journey is underway.
                // With no current status ("Not Set"/"Matched (old)") every entry stage
                // would read "Next", which is noisy — so suppress badges entirely.
                const b = currentStatus ? badge[step.state] : null;
                const expanded = expandedStageIds.includes(step.stageId);
                const expandable = ENABLE_ALL_STATUSES || step.state !== 'upcoming';
                return (
                  <div key={step.stageId}>
                    <button
                      type="button"
                      onClick={() => expandable && toggleStage(step.stageId)}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors ${
                        step.state === 'current' ? 'border-blue-200 bg-blue-50/40'
                        : step.state === 'completed' ? 'border-green-100 bg-green-50/40'
                        : 'border-gray-200 bg-white'
                      } ${expandable ? 'hover:border-gray-300 cursor-pointer' : 'cursor-default'}`}
                    >
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${iconCls[step.state]}`}>
                        {step.state === 'completed' ? (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        )}
                      </span>
                      <span className="flex-1 text-left text-xs font-semibold truncate text-gray-800">
                        {step.stageName}
                      </span>
                      {b && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>}
                      {expandable && (
                        <svg className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      )}
                    </button>

                    {expanded && expandable && (
                      <div className="mt-1 ml-2.5 pl-2.5 border-l border-gray-100 space-y-1">
                        {step.rounds
                          ? step.rounds.map((r) => renderRound(step.stageId, r))
                          : step.flatItems?.map(renderStatusRow)}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Old (legacy) status — read-only reference from the DB (application_status).
                  Shown when the record has no NEW status yet, so the recruiter can see
                  what the old value was and pick a new status from the list above. */}
              {!isLoading && !isError && !current && !!oldStatus && (
                <div className="pt-1.5 mt-1 border-t border-dashed border-gray-200">
                  <div className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Old status (read-only)
                  </div>
                  <div
                    aria-disabled="true"
                    title="Legacy status from the old system — pick a new status above to migrate this candidate"
                    className="w-full px-2.5 py-1.5 text-left text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-700 flex items-center justify-between cursor-not-allowed"
                  >
                    <span className="flex items-center gap-1.5 truncate">
                      <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                      <span className="truncate">{prettifyOldStatus(oldStatus!)}</span>
                    </span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 shrink-0">old</span>
                  </div>
                  <p className="px-1 mt-1.5 text-[11px] text-gray-400 leading-snug">
                    This is the previous status. Select a new status above to migrate this candidate.
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Mandatory comment modal (AC3/AC5) — required for every change incl. Hold/Reject/Withdraw */}
      {pendingStatus && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/30" onClick={handleCancelModal} />
          <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-sm p-4">
            <div className="text-xs text-gray-500">Update status to</div>
            <div className="flex items-center gap-2 mt-1 mb-3">
              <span className={`w-2 h-2 rounded-full ${getStageColor(pendingStatus.stageCode).dot}`} />
              <span className="text-sm font-semibold text-gray-900">{pendingStatus.statusName}</span>
            </div>

            <label className="block text-xs font-medium text-gray-700 mb-1">
              Comment / Reason{' '}
              {isNegativeStatus(pendingStatus.statusCode)
                ? <span className="text-red-500">*</span>
                : <span className="text-gray-400 font-normal">(optional)</span>}
            </label>
            <textarea
              autoFocus
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder={isNegativeStatus(pendingStatus.statusCode)
                ? 'Enter the reason for this status change…'
                : 'Add an optional note…'}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            <div className="flex items-center justify-between mt-1">
              <span className="text-[11px] text-gray-400">{isNegativeStatus(pendingStatus.statusCode) ? 'Required' : 'Optional'}</span>
              <span className="text-[11px] text-gray-400">{comment.length}/500</span>
            </div>

            <div className="flex items-center justify-end gap-2 mt-3">
              <button onClick={handleCancelModal} disabled={isUpdating}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleConfirm}
                disabled={(isNegativeStatus(pendingStatus.statusCode) && !comment.trim()) || isUpdating}
                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {isUpdating && <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationStatusDropdown;
